/**
 * One hatched "Unavailable" strip on a car lane, with drag-to-resize handles on
 * each edge (vendor scope only). Resize gestures bubble up to the parent via
 * `onResizePointerDown(carId, range, edge, ev)`.
 */
import { addDaysToIso, dayOffset, COL_W } from "@/components/calendar/calendarHelpers";

export default function CalendarUnavailStrip({
  car,
  range,
  scope,
  resizing,
  windowStart,
  windowDays,
  onResizePointerDown,
  onEdit,
}) {
  const rid = range.id || range.start;
  const isResizing = resizing && resizing.carId === car.id && resizing.rangeId === rid;

  // While dragging, override the strip's bounds with the live cursor delta.
  const liveStart = isResizing && resizing.edge === "left"  ? addDaysToIso(range.start, resizing.deltaDays) : range.start;
  const liveEnd   = isResizing && resizing.edge === "right" ? addDaysToIso(range.end,   resizing.deltaDays) : range.end;

  const s = dayOffset(liveStart, windowStart);
  const e = dayOffset(liveEnd, windowStart);
  if (e === null || s === null || e <= 0 || s >= windowDays) return null;

  const left = Math.max(0, s) * COL_W;
  const right = Math.min(windowDays, e) * COL_W;

  return (
    <div
      className={`absolute top-1 bottom-1 rounded-md border bg-rose-50 hover:bg-rose-100 transition ${scope === "vendor" ? "cursor-pointer" : "cursor-help"} ${isResizing ? "border-rose-500 ring-2 ring-rose-300" : "border-rose-300/70"}`}
      style={{
        left,
        width: right - left,
        backgroundImage: "repeating-linear-gradient(45deg, transparent 0 4px, rgba(244,63,94,0.12) 4px 8px)",
      }}
      onClick={(ev) => {
        if (scope !== "vendor" || isResizing) return;
        ev.stopPropagation();
        onEdit?.(car.id, range);
      }}
      title={`Unavailable · ${range.reason || "vendor blocked"}${scope === "vendor" ? " · click to edit, drag the edges to resize" : ""}`}
      data-testid={`cal-unavail-${car.id}-${range.start}`}
    >
      <div className="text-[10px] text-rose-800 font-medium px-1.5 py-0.5 truncate pointer-events-none">
        {range.reason || "Unavailable"}
      </div>
      {scope === "vendor" && (
        <>
          <div
            onPointerDown={(ev) => onResizePointerDown(ev, car.id, range, "left")}
            onClick={(ev) => ev.stopPropagation()}
            className="absolute left-0 top-0 bottom-0 w-3.5 -ml-1 cursor-ew-resize hover:bg-rose-400/50 rounded-l-md touch-none"
            title="Drag to change start date"
            data-testid={`cal-resize-left-${car.id}-${range.start}`}
          />
          <div
            onPointerDown={(ev) => onResizePointerDown(ev, car.id, range, "right")}
            onClick={(ev) => ev.stopPropagation()}
            className="absolute right-0 top-0 bottom-0 w-3.5 -mr-1 cursor-ew-resize hover:bg-rose-400/50 rounded-r-md touch-none"
            title="Drag to change end date"
            data-testid={`cal-resize-right-${car.id}-${range.start}`}
          />
          {isResizing && resizing.deltaDays !== 0 && (
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-rose-700 bg-white border border-rose-200 rounded px-1.5 py-0.5 whitespace-nowrap shadow-sm">
              {resizing.deltaDays > 0 ? "+" : ""}{resizing.deltaDays} day{Math.abs(resizing.deltaDays) === 1 ? "" : "s"}
            </div>
          )}
        </>
      )}
    </div>
  );
}
